/*
  RYNOR product zoom
  ------------------------------------------------------------------
  Opens the theme's PhotoSwipe (assets/vendor-zoom.js) for product photos:

  - The photo runs exactly from the top of the window to the bottom and is
    centred on every window size. PhotoSwipe does all the positioning; no
    CSS resizes or moves the photo (that is what pushed it off-centre before).
  - The page background is one solid colour on both sides. When a photo's
    edges are white or a very light grey, the page takes that exact colour so
    the photo blends in. Anything else (skin, hair, colours, darker greys,
    see-through) uses the colour from Theme settings > Product zoom (#f1f1f1).
  - The prev / close / next buttons live in snippets/snip-photoswipe.liquid.

  Used by sections/main-product.liquid:
    window.RynorZoom.open(arrayOfGalleryImgElements, startIndex)
*/
(function () {
  'use strict';

  if (window.RynorZoom) return;

  var HEADROOM = 3;     // the fitted photo is 1/3 of its zoomable size, so zooming in never hits a limit
  var TAP_ZOOM = 2.5;   // click / double-tap zooms in 2.5x
  var SAMPLE_W = 48;    // width of the tiny copy used to read edge colours
  var STRIPS = 8;       // each edge is read as 8 strips
  var DEPTH = 2;        // how many pixels in from the edge are read
  var TOL = 12;         // colour tolerance inside one strip
  var CHROMA_MAX = 10;  // more difference than this between R, G and B counts as a colour (skin, hair, beige...)
  var MIN_STRIPS = 3;   // at least 3 of the 16 strips on the visible edges must be white / light grey to match

  var samples = typeof WeakMap === 'function' ? new WeakMap() : null;
  var current = null;
  var restoreFocusTo = null;
  var savedBodyPadding = null;

  /* ---------- small helpers ---------- */

  function num(value) {
    var n = parseFloat(value);
    return isFinite(n) && n > 0 ? n : 0;
  }

  // "Darkest grey to match" setting: greys lighter than this (and white) get matched.
  function lightnessFloor(value) {
    var hex = /^#?([0-9a-f]{6})$/i.exec((value || '').trim());
    if (!hex) return 220;
    var n = parseInt(hex[1], 16);
    return Math.round(((n >> 16 & 255) + (n >> 8 & 255) + (n & 255)) / 3);
  }

  function config(root) {
    var data = root.dataset || {};
    return {
      fallback: (data.ryZoomBg || '').trim() || '#f1f1f1',
      match: data.ryZoomMatch !== 'false',
      floor: lightnessFloor(data.ryZoomLimit),
      narrow: data.ryZoomNarrow === 'fit' ? 'fit' : 'fill'
    };
  }

  function rgb(c) {
    return 'rgb(' + c[0] + ',' + c[1] + ',' + c[2] + ')';
  }

  function median(values) {
    values.sort(function (a, b) { return a - b; });
    var mid = values.length >> 1;
    return values.length % 2 ? values[mid] : Math.round((values[mid - 1] + values[mid]) / 2);
  }

  function colourGap(a, b) {
    return Math.max(Math.abs(a[0] - b[0]), Math.abs(a[1] - b[1]), Math.abs(a[2] - b[2]));
  }

  /* ---------- reading a photo's edge colours ---------- */

  // Splits one edge into strips and returns each strip's colour,
  // or null for a strip that is mostly see-through.
  function readEdge(data, w, h, side) {
    var vertical = side === 'left' || side === 'right';
    var length = vertical ? h : w;
    var depth = Math.min(DEPTH, vertical ? w : h);
    var strips = [];

    for (var s = 0; s < STRIPS; s++) {
      var from = Math.floor(s * length / STRIPS);
      var to = Math.max(from + 1, Math.floor((s + 1) * length / STRIPS));
      var r = [], g = [], b = [], pixels = [], clear = 0;

      for (var t = from; t < to; t++) {
        for (var k = 0; k < depth; k++) {
          var x = side === 'left' ? k : side === 'right' ? w - 1 - k : t;
          var y = side === 'top' ? k : side === 'bottom' ? h - 1 - k : t;
          var i = (y * w + x) * 4;
          if (data[i + 3] < 200) { clear++; continue; }
          r.push(data[i]); g.push(data[i + 1]); b.push(data[i + 2]);
          pixels.push([data[i], data[i + 1], data[i + 2]]);
        }
      }

      if (!pixels.length || pixels.length < clear) { strips.push(null); continue; }

      var mid = [median(r), median(g), median(b)];
      var close = 0;
      for (var p = 0; p < pixels.length; p++) {
        if (colourGap(pixels[p], mid) <= TOL) close++;
      }
      strips.push({ c: mid, even: close / pixels.length >= 0.6 });
    }
    return strips;
  }

  // White or light grey: bright enough and close to neutral.
  function lightNeutral(c, floor) {
    var lo = Math.min(c[0], c[1], c[2]);
    var hi = Math.max(c[0], c[1], c[2]);
    return lo >= floor && hi - lo <= CHROMA_MAX;
  }

  // One colour for both sides: the middle value of every even, white / light grey
  // strip on the visible edges. Null when the edges aren't white or light grey.
  function matchColour(edges, floor) {
    var good = [];
    edges.forEach(function (strips) {
      (strips || []).forEach(function (strip) {
        if (strip && strip.even && lightNeutral(strip.c, floor)) good.push(strip.c);
      });
    });
    if (good.length < MIN_STRIPS) return null;
    return rgb([0, 1, 2].map(function (k) {
      return median(good.map(function (c) { return c[k]; }));
    }));
  }

  function analyse(data, w, h) {
    return {
      left: readEdge(data, w, h, 'left'),
      right: readEdge(data, w, h, 'right'),
      top: readEdge(data, w, h, 'top'),
      bottom: readEdge(data, w, h, 'bottom')
    };
  }

  // Returns the analysis, null when nothing could be read, or false when the
  // browser blocks reading the pixels (image from another domain).
  function readImage(img) {
    var nw = img.naturalWidth;
    var nh = img.naturalHeight;
    if (!nw || !nh) return null;

    var w = SAMPLE_W;
    var h = Math.max(STRIPS, Math.min(SAMPLE_W * 4, Math.round(SAMPLE_W * nh / nw)));
    var canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    var ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;

    try {
      ctx.drawImage(img, 0, 0, w, h);
      return analyse(ctx.getImageData(0, 0, w, h).data, w, h);
    } catch (err) {
      return false;
    }
  }

  function whenLoaded(img) {
    if (img.complete) return Promise.resolve(!!img.naturalWidth);
    return new Promise(function (resolve) {
      var timer = setTimeout(function () { finish(false); }, 6000);
      function finish(ok) {
        clearTimeout(timer);
        img.removeEventListener('load', onLoad);
        img.removeEventListener('error', onError);
        resolve(ok);
      }
      function onLoad() { finish(!!img.naturalWidth); }
      function onError() { finish(false); }
      img.addEventListener('load', onLoad);
      img.addEventListener('error', onError);
    });
  }

  // Backup route: fetch a tiny CORS-enabled copy of the photo from Shopify's CDN.
  function readRemote(src) {
    return new Promise(function (resolve) {
      var url;
      try { url = new URL(src, window.location.href); } catch (err) { resolve(null); return; }
      url.searchParams.set('width', String(SAMPLE_W * 2));
      url.searchParams.delete('height');
      url.searchParams.delete('crop');

      var probe = new Image();
      var timer = setTimeout(function () { done(null); }, 6000);
      function done(result) {
        clearTimeout(timer);
        probe.onload = probe.onerror = null;
        resolve(result);
      }
      probe.crossOrigin = 'anonymous';
      probe.onload = function () { done(readImage(probe) || null); };
      probe.onerror = function () { done(null); };
      probe.src = url.href;
    });
  }

  function sampleFor(item) {
    var img = item.ryImg;
    if (!img) return Promise.resolve(null);
    if (samples && samples.has(img)) return samples.get(img);

    var job = whenLoaded(img).then(function (loaded) {
      var result = loaded ? readImage(img) : null;
      return result ? result : readRemote(item.src);
    }).catch(function () { return null; });

    if (samples) samples.set(img, job);
    return job;
  }

  /* ---------- sizing (runs inside PhotoSwipe before it fits each photo) ---------- */

  function sizeItem(ps, item, cfg) {
    var vw = ps.viewportSize.x;
    var vh = ps.viewportSize.y;
    var photo = item.ryAspect;
    if (!vw || !vh || !photo) return;

    var screen = vw / vh;

    if (photo <= screen) {
      // Window is wider than the photo: full height, background shows left and right.
      item.ryLayout = 'pillar';
      item.h = vh * HEADROOM;
      item.w = item.h * photo;
    } else if (cfg.narrow === 'fill') {
      // Window is narrower (phones): keep full height, trim the sides evenly.
      item.ryLayout = 'cover';
      item.w = vw * HEADROOM;
      item.h = vh * HEADROOM;
    } else {
      // Window is narrower and "Show whole photo" is chosen: full width, background above and below.
      item.ryLayout = 'letter';
      item.w = vw * HEADROOM;
      item.h = item.w / photo;
    }
  }

  /* ---------- painting the background ---------- */

  function backgroundFor(item, cfg) {
    var sample = cfg.match ? item.rySample : null;
    if (!sample) return cfg.fallback;
    // Desktop shows the page beside the photo; "Show whole photo" on phones shows it above and below.
    var edges = item.ryLayout === 'letter' ? [sample.top, sample.bottom] : [sample.left, sample.right];
    return matchColour(edges, cfg.floor) || cfg.fallback;
  }

  // Each slide carries its own background colour, so it moves with the photo while swiping.
  function paint(ps, root, cfg) {
    (ps.itemHolders || []).forEach(function (holder) {
      var item = holder.item;
      var showing = !!(item && item.container && item.container.parentNode === holder.el);
      holder.el.style.setProperty('--ry-slide-bg', showing ? backgroundFor(item, cfg) : cfg.fallback);
    });

    root.style.setProperty('--ry-zoom-current', ps.currItem ? backgroundFor(ps.currItem, cfg) : cfg.fallback);
  }

  /* ---------- page scroll lock ---------- */

  function lockScroll() {
    var html = document.documentElement;
    var body = document.body;
    var scrollbar = window.innerWidth - html.clientWidth;

    if (scrollbar > 0) {
      savedBodyPadding = body.style.paddingRight;
      var padding = parseFloat(window.getComputedStyle(body).paddingRight) || 0;
      body.style.paddingRight = (padding + scrollbar) + 'px';
    }
    html.classList.add('ry-zoom-lock');
  }

  function unlockScroll() {
    document.documentElement.classList.remove('ry-zoom-lock');
    if (savedBodyPadding !== null) {
      document.body.style.paddingRight = savedBodyPadding;
      savedBodyPadding = null;
    }
  }

  /* ---------- buttons ---------- */

  function bindControls(root) {
    if (root.getAttribute('data-ry-controls') === 'bound') return;
    root.setAttribute('data-ry-controls', 'bound');

    root.addEventListener('click', function (event) {
      var button = event.target && event.target.closest ? event.target.closest('[data-ry-zoom]') : null;
      if (!button || !current) return;
      event.preventDefault();

      var action = button.getAttribute('data-ry-zoom');
      if (action === 'prev') current.prev();
      else if (action === 'next') current.next();
      else if (action === 'close') current.close();
    });
  }

  /* ---------- open ---------- */

  function buildItems(imgs) {
    var items = [];
    imgs.forEach(function (img) {
      if (!img) return;
      var src = img.getAttribute('data-full-src') || img.currentSrc || img.getAttribute('src') || '';
      if (!src) return;

      var w = num(img.getAttribute('data-full-width')) || num(img.getAttribute('width')) || img.naturalWidth || 4;
      var h = num(img.getAttribute('data-full-height')) || num(img.getAttribute('height')) || img.naturalHeight || 5;

      items.push({
        src: src,
        msrc: img.currentSrc || img.getAttribute('src') || '',   // already-loaded copy, shown instantly while the big one loads
        w: w,
        h: h,
        title: img.getAttribute('alt') || '',
        ryAspect: w / h,
        ryImg: img
      });
    });
    return items;
  }

  function open(imgs, startIndex) {
    if (current) return current;
    if (typeof window.PhotoSwipe === 'undefined' || typeof window.PhotoSwipeUI_Default === 'undefined') return null;

    var root = document.querySelector('.pswp');
    if (!root) return null;

    var items = buildItems(Array.prototype.slice.call(imgs || []));
    if (!items.length) return null;

    var cfg = config(root);
    var index = Math.max(0, Math.min(items.length - 1, parseInt(startIndex, 10) || 0));
    var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    var duration = reduceMotion ? 0 : 333;

    bindControls(root);
    root.setAttribute('data-ry-count', String(items.length));
    restoreFocusTo = document.activeElement;

    // Lock the page first so PhotoSwipe measures the window without a scrollbar.
    lockScroll();
    document.body.classList.add('ry-pswp-open');

    var ps = new window.PhotoSwipe(root, window.PhotoSwipeUI_Default, items, {
      index: index,
      history: false,
      bgOpacity: 1,
      showHideOpacity: true,
      showAnimationDuration: duration,
      hideAnimationDuration: duration,
      spacing: 0,
      closeOnScroll: false,
      // No reserved toolbar space: this is what left the gap at the top before.
      barsSize: { top: 0, bottom: 0 },
      captionEl: false,
      closeEl: false,
      fullscreenEl: false,
      zoomEl: false,
      shareEl: false,
      counterEl: false,
      arrowEl: false,
      preloaderEl: false,
      tapToClose: false,
      tapToToggleControls: false,
      clickToCloseNonZoomable: false,
      getDoubleTapZoom: function (isMouseClick, item) {
        return Math.min(1, item.initialZoomLevel * TAP_ZOOM);
      }
    });
    current = ps;

    // PhotoSwipe asks for each photo's margins right before it fits the photo
    // to the window (on open, on every resize and on rotation). Sizing there
    // means the fit always lands exactly top to bottom.
    ps.listen('parseVerticalMargin', function (item) { sizeItem(ps, item, cfg); });

    var queued = false;
    function schedulePaint() {
      if (queued) return;
      queued = true;
      Promise.resolve().then(function () {
        queued = false;
        if (current === ps) paint(ps, root, cfg);
      });
    }
    ['gettingData', 'beforeChange', 'afterChange', 'resize', 'initialZoomInEnd', 'imageLoadComplete'].forEach(function (name) {
      ps.listen(name, schedulePaint);
    });

    // Swipe-down / pinch to close: fade the slide backgrounds together with PhotoSwipe's backdrop.
    function setFade(opacity) {
      root.classList.toggle('ry-zoom-dragging', opacity < 1);
      root.style.setProperty('--ry-zoom-alpha', String(opacity));
    }
    ps.listen('onVerticalDrag', setFade);
    ps.listen('onPinchClose', setFade);
    ps.listen('zoomGestureEnded', function () { setFade(1); });

    ps.listen('destroy', function () {
      if (current === ps) current = null;
      (ps.itemHolders || []).forEach(function (holder) { holder.el.style.removeProperty('--ry-slide-bg'); });
      root.classList.remove('ry-zoom-dragging');
      unlockScroll();
      document.body.classList.remove('ry-pswp-open');

      var target = restoreFocusTo;
      restoreFocusTo = null;
      if (target && target.focus && document.contains(target)) {
        try { target.focus({ preventScroll: true }); } catch (err) { /* ignore */ }
      }
    });

    if (cfg.match) {
      items.forEach(function (item) {
        sampleFor(item).then(function (result) {
          item.rySample = result || null;
          if (current === ps) schedulePaint();
        });
      });
    }

    ps.init();
    paint(ps, root, cfg);
    return ps;
  }

  window.RynorZoom = {
    open: open,
    current: function () { return current; }
  };
})();
